import { ref, computed, watch } from 'vue'
import {
  type DirectoryItem,
  type Header,
  type Command,
  type ActionResult,
  type Result,
  ok,
  error,
  isOk,
  ManagerStateUpdate,
  DirectoryItemType
} from './types'
import { useI18n } from 'vue-i18n'
import { DeleteAction } from './actions/deleteAction'
import { ExecutableAction } from './actions/action'
import { EditAction } from './actions/editAction'
import { MoveAction } from './actions/moveAction'
import { DownloadAction } from './actions/downloadAction'
import { ArchiveAction } from './actions/archiveAction'
import { useStore } from 'vuex'
import { OpenAction } from './actions/openAction'
import { useDirectoryTree } from './useDirectoryTree'
import { usePrivileges } from './privileges'
import { storeToRefs } from 'pinia'
import { Pageable } from './files.service'
import { AxiosError } from 'axios'
import * as Sentry from '@sentry/vue'

export type FileManagerState = 'idle' | 'loading' | 'filtering' | 'message' | 'error'

const PAGE_SIZE = 10

const SMALL_HEADERS: Header[] = [{ key: 'name', translationKey: 'name', sortDirection: 'asc' }]
const FULLSCREEN_HEADERS: Header[] = [
  { key: 'name', translationKey: 'name', sortDirection: 'asc' },
  { key: 'uploadDate', translationKey: 'uploaded' },
  { key: 'fileSize', translationKey: 'size' },
  { key: 'entityConnection', translationKey: 'placement', sortable: false }
]

// Used, but only for type checking
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const availableActionClasses = [DeleteAction, EditAction, MoveAction]

type ActionConstructorUnion = (typeof availableActionClasses)[number]

type FileActionError =
  | {
      message: string
      itemAlreadyExists: false
    }
  | {
      itemAlreadyExists: boolean
      suggestedName?: string
    }

type HandlerMap = {
  [K in ActionConstructorUnion['actionName']]?: (
    action: InstanceType<Extract<ActionConstructorUnion, { actionName: K }>>
  ) => void | Promise<void>
}

export function useFileManager<E extends Partial<object> = object>({
  nodeFetcher,
  actionEvents,
  directoryId
}: {
  nodeFetcher?: (options: {
    directoryId: string
    searchTerm: string
    sortHeaders: Header[]
    page: number
    size: number
  }) => Promise<Pageable<DirectoryItem<DirectoryItemType, E>>>
  actionEvents?: Partial<HandlerMap>
  directoryId?: string
} = {}) {
  const tree = useDirectoryTree<E>()

  const currentDirectoryId = ref<string>(directoryId ?? tree.tree.value.id)
  const breadcrumbs = ref<DirectoryItem<'directory', E>[]>([])
  const allItemsInCurrentDir = ref<DirectoryItem<DirectoryItemType, E>[]>([])
  const displayedItems = ref<DirectoryItem<DirectoryItemType, E>[]>([])
  const selectedItems = ref<DirectoryItem<DirectoryItemType, E>[]>([])
  const searchTerm = ref<string>('')
  const sortHeaders = ref<Header[]>(SMALL_HEADERS)
  const state = ref<FileManagerState>('idle')
  const message = ref<string>()
  const currentCommand = ref<Command>()
  const { t } = useI18n()
  const store = useStore()
  const { allowedActions, allowHidden } = storeToRefs(usePrivileges())
  const currentPageNumber = ref<number>(0)
  const currentPage = ref<Pageable<DirectoryItem<DirectoryItemType, E>>>()

  const isLoading = computed(() => ['loading', 'filtering'].includes(state.value))

  const currentDirectory = computed<DirectoryItem<'directory', E>>(
    () =>
      (tree.findNodeById(currentDirectoryId.value) ?? tree.getRootNode()) as DirectoryItem<
        'directory',
        E
      >
  )

  const filteredDisplayedItems = computed(() =>
    displayedItems.value.filter((item) => item.archived !== true || allowHidden.value)
  )

  watch(
    [isLoading, state],
    ([isLoading, state]) => {
      if (isLoading) {
        message.value = createLoadingMessage(
          currentCommand.value
            ? `general__text__${currentCommand.value.action}-action`
            : `general__text__${state}`
        )
      }
    },
    { immediate: true }
  )

  const createLoadingMessage = (key?: string) => `${t(key ?? 'general__text__loading')}...`

  async function fetchItemsForDirectory(directoryId: string): Promise<ActionResult> {
    if (!currentPage.value) {
      state.value = 'loading'
      message.value = createLoadingMessage()
    }
    currentCommand.value = undefined
    try {
      let nodes: DirectoryItem<DirectoryItemType, E>[] = []
      updateBreadcrumbs()
      if (nodeFetcher) {
        currentPage.value = await nodeFetcher({
          directoryId,
          searchTerm: searchTerm.value,
          sortHeaders: sortHeaders.value,
          page: currentPageNumber.value,
          size: PAGE_SIZE
        })
        nodes = currentPage.value.content ?? []
        nodes.forEach((node) => {
          node.parentId = directoryId
        })
        // Add the items to the current directory
        tree.addChildrenOfNodeById(directoryId, nodes)
      }

      if (!nodeFetcher) {
        applySearchAndSort()
      }
      const node = tree.findNodeById(directoryId) as DirectoryItem<'directory', E> | null
      displayedItems.value = node?.children || []
      return {
        status: 'success',
        data: undefined
      }
    } catch (e) {
      console.error('Failed to fetch items:', e)
      return {
        status: 'error',
        message: `Failed to fetch items for directory ${currentDirectoryId.value}`,
        error: e instanceof Error ? e : undefined
      }
    }
  }

  function updateBreadcrumbs() {
    const crumbs: DirectoryItem<'directory', E>[] = []
    let currentNode = tree.findNodeById(currentDirectoryId.value)
    while (currentNode) {
      crumbs.unshift({
        id: currentNode.id,
        name: currentNode.name,
        type: currentNode.type
      } as DirectoryItem<'directory', E>)
      if (!currentNode.parentId) break
      currentNode = tree.findNodeById(currentNode.parentId)
    }
    breadcrumbs.value = crumbs
  }

  async function applySearchAndSort() {
    let items = [...displayedItems.value]

    if (searchTerm.value?.length) {
      const lowerSearchTerm = searchTerm.value.toLowerCase()
      items = items.filter((item) => item.name.toLowerCase().includes(lowerSearchTerm))
    }

    if (sortHeaders.value.length > 0) {
      const activeSort = sortHeaders.value.find((h) => h.sortDirection) || sortHeaders.value[0]
      if (activeSort?.key) {
        const sortKey = activeSort.key as keyof DirectoryItem
        const direction = activeSort.sortDirection === 'desc' ? -1 : 1

        items.sort((a, b) => {
          // Prioritize directories
          if (a.type === 'directory' && b.type !== 'directory') return -1
          if (a.type === 'file' && b.type === 'link') return -1
          if (a.type === 'link' && b.type !== 'link') return 1
          if (a.type !== 'directory' && b.type === 'directory') return 1

          const valA = String(a[sortKey] ?? '').toLowerCase()
          const valB = String(b[sortKey] ?? '').toLowerCase()

          if (valA < valB) return -1 * direction
          if (valA > valB) return 1 * direction
          return 0
        })
      }
    }
    displayedItems.value = items
  }

  const debouncedApplySearchAndSort = (() => {
    let timeout: ReturnType<typeof setTimeout>
    return (newSearchTerm: string) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        searchTerm.value = newSearchTerm
      }, 300)
    }
  })()

  // --- Actions ---
  function handleSearch(newSearchTerm: string) {
    debouncedApplySearchAndSort(newSearchTerm)
  }

  function handleSort(newHeaders: Header[]) {
    sortHeaders.value = newHeaders
  }

  function handleSelectionChange(newSelectedItems: DirectoryItem<DirectoryItemType, E>[]) {
    selectedItems.value = newSelectedItems
  }

  function handleDrop(draggedItemIds: string[], targetItemId: string) {
    const targetItem = tree.findNodeById(targetItemId)
    if (targetItem && targetItem.type === 'directory') {
      const draggedItems = draggedItemIds
        .map((id) => tree.findNodeById(id))
        .filter((item): item is DirectoryItem => !!item)
      executeAction(new MoveAction(draggedItems, targetItemId))
    } else {
      console.warn('Invalid drop target:', targetItemId)
    }
  }

  function getAction(
    action: string,
    items: DirectoryItem[]
  ): Result<ExecutableAction<unknown>> | undefined {
    switch (action) {
      case 'rename': {
        const newName = prompt(`Enter new name for "${items[0].name}":`, items[0].name)
        if (newName && newName.trim() !== '') {
          return ok(new EditAction(items[0], { type: items[0].type, name: newName.trim() }))
        } else {
          return error('Invalid name for renaming item.')
        }
      }
      case 'download':
        return ok(new DownloadAction(items))
      case 'archive':
        return ok(new ArchiveAction(items, true))
      case 'open-directory':
      case 'open-link':
        if (items.length === 1) {
          const item = items[0]
          return ok(new OpenAction(item))
        }
        return error('Open action requires exactly one item.')
      case 'back':
        if (currentDirectory.value.parentId) {
          const node = tree.findNodeById(currentDirectory.value.parentId)
          if (node) {
            return ok(new OpenAction(node))
          }
        }
    }
  }

  async function refresh() {
    currentPageNumber.value = 0
    currentPage.value = undefined
    const directoryId = currentDirectoryId.value
    tree.clearChildrenOfNodeById(directoryId)
    const fetchResult = await fetchItemsForDirectory(directoryId)
    updateStateFromActionResult(fetchResult)
  }

  async function updateState(update: ManagerStateUpdate) {
    if (update.currentDirectoryId && currentDirectoryId.value !== update.currentDirectoryId) {
      currentDirectoryId.value = update.currentDirectoryId
    }
    state.value = update.state ?? 'idle'
  }

  async function executeAction<E, U>(
    action: ExecutableAction<E, U>
  ): Promise<Result<E, FileActionError>> {
    state.value = 'loading'
    const result = await action.execute(tree)
    try {
      await actionEvents?.[action.actionName]?.(action)
    } catch (e) {
      Sentry.captureException(e)
      result.status = 'error'
      result.message = `Failed to execute action event for ${action.actionName}`
    }
    if (
      result.status === 'error' &&
      result.error instanceof AxiosError &&
      result.error?.response?.status === 409
    ) {
      console.error('Action failed:', result.message, result.error)
      const suggestedName = (result.error?.response?.data as { suggestedName?: string })
        .suggestedName
      return { ok: false, error: { itemAlreadyExists: true, suggestedName } }
    }
    updateStateFromActionResult(result)
    if (result?.status === 'success') {
      if (result?.update) {
        updateState(result?.update)
      } else {
        state.value = 'idle'
      }
      currentCommand.value = undefined
      return ok(result.data)
    }
    return error({ message: result.message, itemAlreadyExists: false })
  }

  function updateStateFromActionResult(result: ActionResult<unknown> | undefined) {
    if (result) {
      if (result.status === 'success') {
        if (result.message) {
          message.value = result.message
        }
      } else if (result.status === 'info') {
        // message.value = result.message
        state.value = 'idle'
        store.commit('setInfoMessage', {
          type: 'info',
          message: result.message
        })
      } else if (result.status === 'warning') {
        state.value = 'idle'
        store.commit('setInfoMessage', {
          type: 'warning',
          message: result.message
        })
      } else {
        message.value = result.message ?? 'An error occurred.'
        state.value = 'error'
      }
    }
  }

  async function executeCommand(command: Command) {
    if (!allowedActions.value.includes(command.action)) {
      console.warn(`Action "${command.action}" is not allowed by current privileges.`)
      return
    }

    currentCommand.value = command
    const res = getAction(command.action, command.items)
    if (res) {
      if (isOk(res)) {
        return await executeAction(res.data)
      }
      updateStateFromActionResult({
        status: 'warning',
        message:
          typeof res.error === 'string'
            ? res.error
            : 'An error occurred while executing the action.'
      })
    }
  }

  async function openItem(item: DirectoryItem | string | null) {
    if (typeof item === 'string') {
      item = tree.findNodeById(item) as DirectoryItem | null
    }
    if (!item) {
      console.warn('No item provided to open.')
      return
    }
    await executeCommand({
      action: `open-${item.type}`,
      items: [item]
    })
  }

  async function openDirectory(id: string) {
    currentDirectoryId.value = id
    await refresh()
  }

  function resetCommand() {
    currentCommand.value = undefined
    state.value = 'idle' // Used to reset the state after cancelling a replace or save-copy action.
  }

  function setFullscreen(fullscreen: boolean) {
    sortHeaders.value = fullscreen ? FULLSCREEN_HEADERS : SMALL_HEADERS
  }

  async function loadMoreItems() {
    if (isLoading.value || !nodeFetcher || currentPage.value?.last) return
    currentPageNumber.value += 1
    await fetchItemsForDirectory(currentDirectoryId.value)
  }

  function addPath(path: DirectoryItem<'directory'>[]) {
    if (path.length === 0) return
    const rootNode = tree.getRootNode()
    let parentId = rootNode.id
    for (const item of path) {
      const existingNode = tree.findNodeById(item.id)
      if (existingNode) {
        parentId = existingNode.id
      } else {
        const newNode: DirectoryItem<'directory'> = {
          ...item,
          parentId
        }
        tree.addNode(parentId, newNode)
        parentId = newNode.id
      }
    }
  }

  // --- Watchers ---
  watch(
    currentDirectoryId,
    async (newId, oldId) => {
      if (newId === oldId) return
      currentPageNumber.value = 0
      currentPage.value = undefined
      tree.clearChildrenOfNodeById(newId)
      await fetchItemsForDirectory(newId)
    },
    { immediate: true }
  )

  watch(
    [() => searchTerm, sortHeaders],
    async () => {
      if (nodeFetcher) {
        await refresh()
      }
    },
    { deep: true }
  )

  watch(
    [() => allItemsInCurrentDir.value, searchTerm, sortHeaders],
    async () => {
      if (!nodeFetcher) {
        await applySearchAndSort()
      }
    },
    { deep: true }
  )

  watch(displayedItems, (items) => {
    if (items.length === 0) {
      if (searchTerm.value.length > 0) {
        message.value = t('general__text__search-no-results')
      } else {
        message.value = t('general__data__items-no-display')
      }
      state.value = 'message'
    } else {
      state.value = 'idle'
    }
  })

  return {
    // State
    currentDirectoryId,
    breadcrumbs,
    displayedItems: filteredDisplayedItems,
    selectedItems,
    searchTerm,
    sortHeaders,
    message,
    state,
    isLoading,
    currentCommand,
    currentDirectory,
    currentPage,
    tree,

    // Methods
    executeCommand,
    executeAction,
    openItem,
    openDirectory,
    addPath,
    handleSearch,
    handleSort,
    handleSelectionChange,
    refresh,
    handleDrop,
    resetCommand,
    setFullscreen,
    loadMoreItems
  }
}
